import { useEffect, useRef } from "react";

/*
 * The behaviour every dialog needs, in one place: Escape to close, body scroll
 * lock, initial focus, focus restored to whatever opened it, and Tab kept inside
 * the panel.
 *
 * The markup's UI.open()/closeAll() did the first two and none of the rest — a
 * keyboard user could Tab straight out of an open modal into the page behind it.
 * Doing it properly once here means every modal and drawer in the app gets it.
 *
 * Returns a ref to attach to the dialog panel.
 */
export function useOverlayBehaviour({ open, onClose, closeOnEscape = true }) {
    const panelRef = useRef(null);
    const restoreFocusTo = useRef(null);

    /* ---- Body scroll lock ---------------------------------------------- */
    useEffect(() => {
        if (!open) return;

        // Counted, so two stacked overlays do not fight over the same class.
        const body = document.body;
        const depth = Number(body.dataset.overlayDepth ?? "0") + 1;
        body.dataset.overlayDepth = String(depth);
        body.classList.add("overflow-hidden");

        return () => {
            const next = Number(body.dataset.overlayDepth ?? "1") - 1;
            if (next <= 0) {
                delete body.dataset.overlayDepth;
                body.classList.remove("overflow-hidden");
            } else {
                body.dataset.overlayDepth = String(next);
            }
        };
    }, [open]);

    /* ---- Focus: move in on open, restore on close ----------------------- */
    useEffect(() => {
        if (!open) return;

        restoreFocusTo.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        // Prefer an element the caller marked, then the first focusable, then the
        // panel itself so screen readers land inside the dialog.
        const panel = panelRef.current;
        const target =
            panel?.querySelector("[data-autofocus]") ??
            panel?.querySelector(FOCUSABLE) ??
            panel;

        // A frame later, so the panel is laid out and can actually take focus.
        const id = requestAnimationFrame(() => target?.focus?.());

        return () => {
            cancelAnimationFrame(id);
            restoreFocusTo.current?.focus?.();
        };
    }, [open]);

    /* ---- Escape + Tab trap ---------------------------------------------- */
    useEffect(() => {
        if (!open) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape" && closeOnEscape) {
                e.stopPropagation();
                onClose?.();
                return;
            }

            if (e.key !== "Tab") return;

            const panel = panelRef.current;
            if (!panel) return;

            const focusable = [...panel.querySelectorAll(FOCUSABLE)].filter(
                (el) => el.offsetParent !== null || el === document.activeElement,
            );
            if (focusable.length === 0) {
                e.preventDefault();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            } else if (!panel.contains(document.activeElement)) {
                // Focus escaped some other way — pull it back in.
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [open, onClose, closeOnEscape]);

    return panelRef;
}

const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
].join(",");
